/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import api from 'api'
import { Button, Heading, HFlow, Icon, ModalCloseButton, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { useFlags } from 'config/useFlagsContext'
import { MChatRiscoEnum } from 'graphql/types.generated'
import { reportFormatDate, useHandleImpressao } from 'hooks/impressao/useHandleImpressao'
import { useEffect, useMemo, useRef } from 'react'

import { MChatAccessDocLink } from '../components/MChatAccessDocLink'
import { convertMChatToPerguntaRespostaInput } from '../converter-mchat'
import { MChatResultadoView } from '../mchat-resultado/MChatResultadoView'
import { MChatFieldModel } from '../model-mchat'

interface MChatModalTabResultadoProps {
  values: MChatFieldModel
  atendProfId: ID
  cidadaoId: ID
  isEditing: boolean
  onClickVoltar: () => void
}

export function MChatModalTabResultado(props: Readonly<MChatModalTabResultadoProps>) {
  const { values, atendProfId, cidadaoId, isEditing, onClickVoltar } = props

  const { M_CHAT_RF_ENABLED } = useFlags()
  const theme = useTheme()
  const styles = useMemo(() => createStyles(theme), [theme])
  const backButtonRef = useRef<HTMLButtonElement>()

  const { handleImpressaoPath, handleImpressaoError } = useHandleImpressao()

  const isRiscoMedio = values.risco === MChatRiscoEnum.MEDIO

  const handlePrint = (atendProfId: ID, cidadaoId: ID, mchat: MChatFieldModel) => {
    const input = {
      atendProfId: atendProfId,
      cidadaoId: cidadaoId,
      resultado: mchat.resultado,
      risco: mchat.risco,
      aplicacao: convertMChatToPerguntaRespostaInput(mchat),
    }

    handleImpressaoPath({
      apiPath: api.mchat.imprimirMChat,
      values: input,
      file: `MChat.pdf`,
      dateFormat: reportFormatDate,
    }).catch(handleImpressaoError)
  }

  useEffect(() => {
    backButtonRef?.current.focus()
  }, [])

  return (
    <VFlow>
      <HFlow alignItems='flex-start' justifyContent='space-between'>
        <Button kind='normal' size='small' style={styles.voltarButton} onClick={onClickVoltar} innerRef={backButtonRef}>
          <Icon icon='arrowLeft' style={styles.icons} />
          {isEditing ? 'Voltar à edição' : 'Editar a avaliação'}
        </Button>
        <ModalCloseButton />
      </HFlow>
      <VFlow style={styles.modalSectionBody}>
        <HFlow alignItems='center' justifyContent='space-between'>
          <Heading level={1}>Resultado M-CHAT-R™</Heading>
          <Button
            kind='normal'
            size='small'
            style={styles.imprimirButton}
            onClick={(event) => {
              event.stopPropagation()
              handlePrint(atendProfId, cidadaoId, values)
            }}
          >
            <Icon icon='printerOutline' style={styles.icons} />
            Imprimir resultado
          </Button>
        </HFlow>
        <MChatResultadoView isInsideModal showEscala value={values} />
        {isRiscoMedio && (
          <VFlow vSpacing={0.5}>
            <HFlow>
              <HFlow hSpacing={0.5} alignItems='center'>
                <Icon icon='exclamationTriangleOutline' fill='alert' />
                <Heading level={4} color='alert'>
                  Entrevista de seguimento
                </Heading>
              </HFlow>
              {!M_CHAT_RF_ENABLED && <MChatAccessDocLink />}
            </HFlow>
            <Text>
              {!M_CHAT_RF_ENABLED && 'Acesse o documento original para aplicar a avaliação. '}O teste é considerado
              positivo (risco para TEA) se a criança falhar em quaisquer 2 itens na Entrevista de Seguimento.
            </Text>
          </VFlow>
        )}
      </VFlow>
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  icons: css`
    margin-right: 0.5rem;
  `,
  voltarButton: css`
    margin-bottom: 1rem;
  `,
  imprimirButton: css`
    color: ${theme.pallete.primary.main};
    border-color: ${theme.pallete.primary.main};
  `,
  modalSectionBody: css`
    padding-right: 1rem;
  `,
})
